<script lang="ts">
const MIN_WIDTH = 320
</script>
<script lang="ts" setup>
import { type VNodeChild, ref, useTemplateRef, watch, onMounted } from 'vue';
import { pxOrValue, hasSlotContent } from '../../utils';

defineSlots<{
  left?: () => VNodeChild;
  right?: () => VNodeChild;
  before?: () => VNodeChild;
  header?: () => VNodeChild;
  footer?: () => VNodeChild;
}>();

const leftColumnWidth = defineModel<number>('leftColumnWidth');
const containerRef = useTemplateRef('container');
const leftColumnRef = useTemplateRef('leftColumn');

const resizing = ref(false);

function applyWidthStyles(width?: number) {
  containerRef.value?.style.setProperty(
    '--grid-template-columns',
    `${typeof width === 'number' ? pxOrValue(width) : 'minmax(0, 1fr)'} auto minmax(0, 1fr)`,
  );
}



function fixWidth(width?: number) {
  if (typeof width !== 'number' || !containerRef.value) {
    return width;
  }

  const maxWidth = containerRef.value.offsetWidth - MIN_WIDTH;
  return Math.max(MIN_WIDTH, Math.min(maxWidth, width));
}

function onResizeHandleMouseDown(event: MouseEvent) {
  if (event.button !== 0 || !containerRef.value) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  const startX = event.clientX;
  const startWidth = leftColumnWidth.value ?? leftColumnRef.value?.offsetWidth ?? 0;

  let width: number | undefined = startWidth;

  let animationFrame: ReturnType<typeof requestAnimationFrame> | null = null;

  function onMouseMove(event: MouseEvent) {
    resizing.value = true;

    if (animationFrame) {
      return;
    }

    animationFrame = requestAnimationFrame(() => {
      width = fixWidth(startWidth + (event.clientX - startX));

      applyWidthStyles(width);

      animationFrame = null;
    });
  }

  function onMouseUp() {
    resizing.value = false;
    leftColumnWidth.value = width;

    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}

watch(leftColumnWidth, (value) => {
  applyWidthStyles(fixWidth(value));
});

onMounted(() => {
  applyWidthStyles(fixWidth(leftColumnWidth.value));
});

// TODO: fix width on window resize
</script>

<template>
  <div :class="[$style.root, { [$style.resizing]: resizing }]">
    <div v-if="hasSlotContent($slots.before)" :class="$style.before">
      <slot name="before" />
    </div>
    <div v-if="hasSlotContent($slots.header)" :class="$style.header">
      <slot name="header" />
    </div>
    <div ref="container" :class="$style.body">
      <div ref="leftColumn" :class="$style.column">
        <slot name="left" />
      </div>
      <div :class="$style.handle" @mousedown="onResizeHandleMouseDown($event)" />
      <div :class="$style.column">
        <slot name="right" />
      </div>
    </div>
    <div v-if="hasSlotContent($slots.footer)" :class="$style.footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<style lang="scss" module>
.resizing .body {
  cursor: col-resize;

  > * {
    pointer-events: none;
  }
}

.handle {
  position: relative;
  width: 2px;
  background-color: #d5d6d9;
  cursor: col-resize;
  pointer-events: all;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -5px;
    width: 12px;
  }
}

.resizing .handle,
.handle:hover {
  background-color: #085cd9;
  transition-property: background-color;
  transition-timing-function: linear;
  transition-duration: 0.1s;
  transition-delay: 0.1s;
}

.column {
  height: 100%;
  overflow: hidden;
  position: relative;
}

.root {
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  border-radius: 10px;
  overflow: hidden;
  overflow-anchor: none;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  border: 1px solid #f0f1f4;
}

.header {
  padding: 8px 8px 12px;
}

.body {
  flex: 1 1 0%;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: var(--grid-template-columns, 1fr auto 1fr);
}

.footer {
  padding: 20px 16px 10px;
}

.header,
.footer {
  min-height: 48px;
  box-sizing: border-box;
}
</style>
